import React, { useState, useRef } from 'react';
import Link from 'next/link';
import getConfig from 'next/config';
import Request from '@apester/requests';
import { useStateValue, setLoading } from '../../state/stateProvider';
import Form from '../../components/Form/Form';
import InputField from '../../components/InputField/InputField';
import trackEvent from '../../utils/event-helper';
import ContactSupportLink from '../../components/ContactSupportLink/ContactSupportLink';
import { isEmail, EMAIL_ERROR, EMAIL_MISSING } from '../../utils/auth-utils';
import styles from '../../styles/resetPassword.module.scss';

const { publicRuntimeConfig: { config } } = getConfig();

const ERROR_MESSAGES = {
  429: `You've sent too many requests. Please try again later.`,
  DEFAULT: 'Invalid Data.',
} as const;

function isApiError(err: unknown): err is { code: number } {
  return typeof err === 'object' && err !== null && 'code' in err;
}

const ForgotPasswordPage = () => {
  const emailInput = useRef(null);

  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string>();
  const [emailError, setEmailError] = useState<string>();
  const [emailValue, setEmailValue] = useState<string>('');

  const [{ isLoading }, dispatch] = useStateValue();

  const validateEmail = () => {
    const email = emailInput.current?.value || '';
    if (!email) {
      setEmailError(EMAIL_MISSING);
      return false;
    }
    if (!isEmail(email)) {
      setEmailError(EMAIL_ERROR);
      return false;
    }
    setEmailError(undefined);
    return true;
  };

  const title = emailSent ?
    'You\'re almost there!'
    : `Forgot your password?
    Let's get you a new one!`;
  const subtitle = emailSent
    ? `We'll send you a link to reset your password.
      Be sure to check your spam folder if you don't receive our email soon.`
    : 'To reset your password, enter the email associated with your Apester channel';

  const actionLabel = emailSent ? 'resend email' : 'continue';

  const onSubmit = async () => {
    if (emailSent) {
      setEmailSent(false);
      return;
    }
    if (!validateEmail()) {
      return;
    }
    const email = emailInput.current.value;
    setLoading(dispatch, true);
    setError(undefined);
    try {
      await Request.Post(new URL(`${config.USERS_URL}/request-new-password`), { email }, { withCredentials: true });
      const eventProperties = {
        browser: navigator.appVersion,
        email,
      };
      trackEvent('password_reset_success', eventProperties);
      setLoading(dispatch, false);
      setEmailSent(true);
    } catch (err: unknown) {
      setEmailSent(false);
      setLoading(dispatch, false);

      const code = isApiError(err) ? err.code : null;
      setError(ERROR_MESSAGES[code] ?? ERROR_MESSAGES.DEFAULT);
    }
  };

  const isFormDisabled = () => (
    isLoading || emailError || !emailValue
  )

  return (
    <div className={`${styles.container} ${styles.forgotPasswordContainer}`}>
        <div className={styles.title} style={{ gridRow: 1 }}>
            {title}
        </div>
        <div className={styles.subtitle} style={{ gridRow: 3 }}>
            {subtitle}
        </div>
        <Form
          isDisabled={isFormDisabled()}
          className={emailSent ? 'almostThere' : 'forgotPassword'}
          style={{ gridRow: 5 }}
          onAction={onSubmit}
          actionLabel={actionLabel}>
            {!emailSent
                && <InputField
                    refElement={emailInput}
                    style = {{ gridRow: 1 }}
                    label="Email"
                    onChange={() => { setEmailValue(emailInput.current?.value || ''); validateEmail(); }}
                    hasError={emailError}/>
            }
        </Form>
        {emailSent ?
            <ContactSupportLink style={{ gridRow: 8 }} />
          : <div className="linkContainer textCenter" style={{ gridRow: 8 }}>
              <Link href="/auth/login">
                  <a className="link">
                      return to log in
                  </a>
              </Link>
            </div>
        }
        {error && (
            <div className="error textCenter" style={{ gridRow: 6, marginTop: 8, marginBottom: 0 }}>
                {error}
            </div>
        )}
    </div>
  );
};

ForgotPasswordPage.pageClass = 'authFrame';
export default ForgotPasswordPage;
